import { CustomErrorResponses } from "@artists/gql/design-challenges"
import type { RefObject } from "react"
import { useState } from "react"
import { useTypedParams } from "react-router-typesafe-routes/dom"

import { useAuth } from "../../api"
import { useFatalErrors } from "../../api/hooks/useFatalError"
import { type Challenge, useVotesQuery } from "../../api/hooks/vote"
import { Redirect, Root } from "../../utils"
import { OffsetContextProvider, Vote, VoteContextProvider } from "./components"
import { UnsavedContextProvider } from "./components/unsaved-context"

interface Props {
	titleRef: RefObject<HTMLDivElement>
	challenge: Maybe<Challenge>
}

export const Content = ({ challenge, titleRef }: Props) => {
	const { user, isDataReceived: isUserDataReceived } = useAuth()
	const { slug } = useTypedParams(Root.Vote)

	const [queryOffset, setQueryOffset] = useState<Nullable<number>>(null)
	const {
		promotion,
		entriesForVoting,
		guestVoting,
		info,
		isDataReceived,
		errors,
		loading,
	} = useVotesQuery(queryOffset)

	// This is an error we don't want to recover from
	useFatalErrors(errors, [CustomErrorResponses.AccessDenied])

	const anonymousAlreadyVoted =
		!user && isUserDataReceived && info?.votingStats.totalVoted

	if (anonymousAlreadyVoted) {
		return <Redirect to={Root.ReviewVotes.buildPath({ slug })} />
	}

	return (
		<UnsavedContextProvider>
			<VoteContextProvider
				previouslySubmittedVotes={info?.votingStats.totalVoted || 0}
				totalEntries={info?.pagination.total || 0}
			>
				<OffsetContextProvider
					queryOffset={queryOffset}
					setQueryOffset={setQueryOffset}
					paginationOffset={info?.pagination.offset}
				>
					<Vote
						errors={errors}
						challenge={challenge}
						guestVoting={guestVoting}
						promotion={promotion}
						entriesForVoting={entriesForVoting}
						entriesRef={titleRef}
						isDataReceived={isDataReceived}
						isAlreadyVoted={!!info?.votingStats.totalVoted}
						loading={loading || !challenge || !isUserDataReceived}
					/>
				</OffsetContextProvider>
			</VoteContextProvider>
		</UnsavedContextProvider>
	)
}
